import * as React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slide,
    Divider,
    Typography,
    CircularProgress,
    IconButton,
    Box,
    ThemeProvider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    PrimaryButton,
    SecondaryButton,
    SubTitleScreen,
    IconContainer,
    DialogTitleText,
    DialogPrimaryButton,
    DialogSecondaryButton
} from "./Styles";
import {tokens, useMode} from "../theme";
import {useTheme} from "@mui/material";
import PropTypes from 'prop-types';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MuiDialog = ({
                       icon,
                       triggerButton,
                       title,
                       subtitle,
                       content,
                       existAction,
                       actionTitle,
                       actionFunction,
                       onClose,
                       isLoading,
                       actionType,
                       formId,
                       maxWidth,
                       disableAction,
                       open: controlledOpen // new prop
                   }) => {
    // Get theme from MUI context
    const theme = useTheme();
    // Access or use the app's theme mode directly to ensure we're getting the current mode
    const [appTheme] = useMode();
    
    // Use appTheme if available, otherwise fall back to the context theme
    const effectiveTheme = appTheme || theme;
    const colors = tokens(effectiveTheme?.palette?.mode || 'light');
    
    const [internalOpen, setInternalOpen] = React.useState(false);
    const isControlled = typeof controlledOpen === 'boolean';
    const open = isControlled ? controlledOpen : internalOpen;

    const handleClickOpen = () => {
        if (!isControlled) setInternalOpen(true);
    };
    const handleClose = (event, reason) => {
        // Do NOT close on backdrop click or Escape to avoid accidental close after file dialog returns
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        if (!isControlled) setInternalOpen(false);
        if (typeof onClose === 'function') onClose();
    };

    return (
        <React.Fragment>
            {triggerButton && (
                <Box onClick={handleClickOpen} sx={{ display: 'inline' }}>
                    {triggerButton}
                </Box>
            )}
            
            {/* Use ThemeProvider to ensure correct theme context without breaking Redux context */}
            <ThemeProvider theme={effectiveTheme}>
                <Dialog
                    open={open}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={handleClose}
                    disableEscapeKeyDown
                    maxWidth={maxWidth || 'sm'}
                    fullWidth
                    aria-describedby="alert-dialog-slide-description"
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                        },
                    }}
                >
                    <DialogTitle sx={{ position: 'relative', pb: subtitle ? 1 : 2 }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: "4px"}}>
                                <div style={IconContainer(colors)}>
                                    {icon ? React.cloneElement(icon, {sx: {fontSize: "27px"}}) : null}
                                </div>
                                <div>
                                    <Typography sx={DialogTitleText(colors)}>{title}</Typography>
                                </div>
                            </Box>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: colors.grey[500],
                                '&:hover': {
                                    color: colors.grey[700],
                                    backgroundColor: colors.grey[100]
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <Divider/>

                {open && (
                    <DialogContent sx={{px: 4, py: 2}}>
                    {subtitle && <Typography sx={{...SubTitleScreen(colors), fontSize: 15}}>
                                    {subtitle}
                                </Typography>}
                        {typeof content === 'function' ? content(open) : content}
                    </DialogContent>
                )}


                <DialogActions>
                    {React.isValidElement(existAction)
                        ? existAction
                        : existAction && (
                            <Button
                                onClick={async (e) => {
                                    // If an explicit actionFunction is provided, use it; otherwise, let the form submit natively
                                    if (typeof actionFunction === 'function') {
                                        try {
                                            const result = await actionFunction();
                                            if (result === true) {
                                                handleClose();
                                            }
                                        } catch (error) {
                                            console.error('Error in dialog action:', error);
                                        }
                                    }
                                }}
                                sx={DialogPrimaryButton(colors)}
                                type={actionType}
                                form={formId}
                                disabled={isLoading || disableAction}
                            >
                                {isLoading ? <CircularProgress size={24}/> : actionTitle}
                            </Button>
                        )
                    }
                </DialogActions>
                </Dialog>
            </ThemeProvider>
        </React.Fragment>
    );
};

MuiDialog.propTypes = {
    triggerButton: PropTypes.element, // now optional
    icon: PropTypes.element,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    existAction: PropTypes.bool,
    actionTitle: PropTypes.string,
    actionFunction: PropTypes.func,
    onClose: PropTypes.func,
    actionType: PropTypes.string,
    formId: PropTypes.string,
    disableAction: PropTypes.bool,
    open: PropTypes.bool // controlled open
};

export default MuiDialog;